import {
  getOperationalOrdersState,
  normalizeSalonReservations,
  normalizeSalonTables,
  saveOperationalOrdersState,
} from "../local/operationalOrders";
import {
  normalizeWaiterOperationsState,
  saveWaiterOperationsState,
  WAITER_OPERATIONS_REMOTE_SYNC_SOURCE,
} from "../local/waiterOperations";
import {
  normalizePaymentOperationsCollection,
  savePaymentOperations,
  PAYMENT_OPERATIONS_REMOTE_SYNC_SOURCE,
} from "../local/paymentOperations";
import {
  normalizeCashRegisterState,
  saveCashRegisterState,
  CASH_REGISTER_REMOTE_SYNC_SOURCE,
} from "../local/cashRegister";
import { normalizeBranchClosingControl } from "../local/branchClosingControl";
import { supabase } from "./client";
import { isSupabaseConfigured } from "./config";
import { withRequestDeadline } from "./requestDeadline";

const EMPTY_OPERATIONAL_STATE = {
  mesasActivas: [],
  pedidosHistoricos: [],
  salonTables: [],
  salonLayout: {},
  salonReservations: {},
  salonRooms: [],
  salonTableRooms: {},
  salonFixtures: {},
  closingControl: normalizeBranchClosingControl(),
};
export const KIOSK_TERMINAL_CLAIM_TTL_MS = 10 * 60 * 1000;
export const KIOSK_TERMINAL_CLAIM_HEARTBEAT_MS = 45 * 1000;
const SHARED_OPERATIONAL_STATE_DUPLICATE_WINDOW_MS = 1200;

let branchOperationalStatePatchRpcAvailable = null;
let branchClosingControlRpcAvailable = null;
const recentSharedOperationalWriteSignatures = new Map();
const sharedOperationalWriteQueues = new Map();

export const subscribeToSharedOperationalStateChanges = (
  branchId,
  listener
) => {
  const safeBranchId = String(branchId || "").trim();

  if (!isSupabaseConfigured || !supabase || !safeBranchId) {
    return () => {};
  }

  const channel = supabase
    .channel(`operational-state:${safeBranchId}:${Date.now()}`)
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "branch_operational_state",
        filter: `branch_id=eq.${safeBranchId}`,
      },
      () => {
        if (typeof listener === "function") listener();
      }
    )
    .subscribe();

  return () => {
    void supabase.removeChannel(channel);
  };
};

const enqueueSharedOperationalWrite = (branchId, writeOperation) => {
  const safeBranchId = String(branchId || "").trim();

  if (!safeBranchId) {
    return Promise.resolve().then(writeOperation);
  }

  const previousWrite = sharedOperationalWriteQueues.get(safeBranchId) || Promise.resolve();
  const queuedWrite = previousWrite.catch(() => null).then(writeOperation);

  sharedOperationalWriteQueues.set(safeBranchId, queuedWrite);
  void queuedWrite
    .finally(() => {
      if (sharedOperationalWriteQueues.get(safeBranchId) === queuedWrite) {
        sharedOperationalWriteQueues.delete(safeBranchId);
      }
    })
    .catch(() => null);

  return queuedWrite;
};

const hasOwnProperty = (payload, key) =>
  Boolean(payload && Object.prototype.hasOwnProperty.call(payload, key));

const isMissingOperationalStatePatchRpcError = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();

  return (
    error?.code === "42883" ||
    error?.code === "PGRST202" ||
    message.includes("could not find the function") ||
    message.includes("patch_branch_operational_state")
  );
};

const isOperationalStateSyncPermissionError = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""} ${error?.hint || ""}`
    .toLowerCase()
    .trim();

  return (
    error?.code === "42501" ||
    message.includes("row-level security policy") ||
    message.includes("violates row-level security policy") ||
    message.includes("permission denied") ||
    message.includes("no tienes permisos para sincronizar el estado operativo")
  );
};

const pickPayloadValue = (payload, camelKey, snakeKey = "") => {
  if (hasOwnProperty(payload, camelKey)) {
    return payload[camelKey];
  }

  if (snakeKey && hasOwnProperty(payload, snakeKey)) {
    return payload[snakeKey];
  }

  return undefined;
};

const normalizeOperationalOrdersState = (payload, fallbackFactory) => {
  const fallbackValue =
    typeof fallbackFactory === "function"
      ? fallbackFactory()
      : EMPTY_OPERATIONAL_STATE;

  return {
    mesasActivas: Array.isArray(payload?.mesasActivas)
      ? payload.mesasActivas
      : fallbackValue.mesasActivas || [],
    pedidosHistoricos: Array.isArray(payload?.pedidosHistoricos)
      ? payload.pedidosHistoricos
      : fallbackValue.pedidosHistoricos || [],
    salonTables: normalizeSalonTables(
      Array.isArray(payload?.salonTables)
        ? payload.salonTables
        : fallbackValue.salonTables || []
    ),
    salonLayout:
      payload?.salonLayout && typeof payload.salonLayout === "object"
        ? payload.salonLayout
        : fallbackValue.salonLayout || {},
    salonReservations: normalizeSalonReservations(
      payload?.salonReservations && typeof payload.salonReservations === "object"
        ? payload.salonReservations
        : fallbackValue.salonReservations || {}
    ),
    salonRooms: Array.isArray(payload?.salonRooms)
      ? payload.salonRooms
      : fallbackValue.salonRooms || [],
    salonTableRooms:
      payload?.salonTableRooms && typeof payload.salonTableRooms === "object"
        ? payload.salonTableRooms
        : fallbackValue.salonTableRooms || {},
    salonFixtures:
      payload?.salonFixtures && typeof payload.salonFixtures === "object"
        ? payload.salonFixtures
        : fallbackValue.salonFixtures || {},
  };
};

const normalizeKioskTerminalStationNumber = (value) => {
  const parsedValue = Math.min(12, Math.max(1, Number(value || 0) || 0));
  return Number.isFinite(parsedValue) ? parsedValue : 0;
};

const normalizeTimestamp = (value, fallback = "") => {
  const safeValue = String(value || "").trim();

  if (!safeValue) {
    return fallback;
  }

  const parsedDate = new Date(safeValue);

  if (Number.isNaN(parsedDate.getTime())) {
    return fallback;
  }

  return parsedDate.toISOString();
};

const EMPTY_SHARED_STATE_SECTION_AVAILABILITY = {
  operationalOrders: false,
  mesasActivas: false,
  pedidosHistoricos: false,
  salonTables: false,
  salonLayout: false,
  salonReservations: false,
  salonRooms: false,
  salonTableRooms: false,
  salonFixtures: false,
  waiterOps: false,
  paymentOperations: false,
  cashRegisterState: false,
  kioskTerminalClaims: false,
  closingControl: false,
};

const normalizeKioskTerminalClaims = (payload) => {
  const rawClaims = pickPayloadValue(
    payload,
    "kioskTerminalClaims",
    "kiosk_terminal_claims"
  );
  const claimsByStation = new Map();

  (Array.isArray(rawClaims) ? rawClaims : []).forEach((claim) => {
    const stationNumber = normalizeKioskTerminalStationNumber(
      claim?.stationNumber ?? claim?.station_number
    );
    const deviceId = String(claim?.deviceId ?? claim?.device_id ?? "")
      .trim()
      .slice(0, 120);

    if (!stationNumber || !deviceId) {
      return;
    }

    const claimedAt = normalizeTimestamp(
      claim?.claimedAt ?? claim?.claimed_at,
      new Date().toISOString()
    );
    const lastSeenAt = normalizeTimestamp(
      claim?.lastSeenAt ?? claim?.last_seen_at,
      claimedAt
    );
    const nextClaim = {
      stationNumber,
      stationLabel: String(
        claim?.stationLabel ?? claim?.station_label ?? `Totem ${stationNumber}`
      )
        .trim()
        .slice(0, 80),
      deviceId,
      deviceLabel: String(claim?.deviceLabel ?? claim?.device_label ?? "")
        .trim()
        .slice(0, 120),
      claimedAt,
      lastSeenAt,
    };
    const previousClaim = claimsByStation.get(stationNumber);

    if (!previousClaim) {
      claimsByStation.set(stationNumber, nextClaim);
      return;
    }

    const previousTimestamp = new Date(previousClaim.lastSeenAt || 0).getTime();
    const nextTimestamp = new Date(nextClaim.lastSeenAt || 0).getTime();

    if (nextTimestamp >= previousTimestamp) {
      claimsByStation.set(stationNumber, nextClaim);
    }
  });

  return Array.from(claimsByStation.values()).sort(
    (left, right) => left.stationNumber - right.stationNumber
  );
};

const pruneInactiveKioskTerminalClaims = (claims, nowDate = new Date()) => {
  const nowTimestamp =
    nowDate instanceof Date ? nowDate.getTime() : new Date(nowDate).getTime();

  if (!Number.isFinite(nowTimestamp)) {
    return normalizeKioskTerminalClaims({ kioskTerminalClaims: claims });
  }

  return normalizeKioskTerminalClaims({
    kioskTerminalClaims: (Array.isArray(claims) ? claims : []).filter((claim) => {
      const lastSeenTimestamp = new Date(claim?.lastSeenAt || 0).getTime();

      if (!Number.isFinite(lastSeenTimestamp)) {
        return false;
      }

      return nowTimestamp - lastSeenTimestamp < KIOSK_TERMINAL_CLAIM_TTL_MS;
    }),
  });
};

const normalizeSharedOperationalState = (payload, fallbackFactory) => {
  const normalizedOrders = normalizeOperationalOrdersState(payload, fallbackFactory);

  return {
    ...normalizedOrders,
    waiterOps: normalizeWaiterOperationsState(
      pickPayloadValue(payload, "waiterOps", "waiter_operations")
    ),
    paymentOperations: normalizePaymentOperationsCollection(
      pickPayloadValue(payload, "paymentOperations", "payment_operations")
    ),
    cashRegisterState: normalizeCashRegisterState(
      pickPayloadValue(payload, "cashRegisterState", "cash_register_state")
    ),
    kioskTerminalClaims: normalizeKioskTerminalClaims(payload),
    closingControl: normalizeBranchClosingControl(
      pickPayloadValue(payload, "closingControl", "closing_control")
    ),
  };
};

const getSharedStateSectionAvailability = (payload) => {
  const operationalFields = {
    mesasActivas: hasOwnProperty(payload, "mesasActivas"),
    pedidosHistoricos: hasOwnProperty(payload, "pedidosHistoricos"),
    salonTables: hasOwnProperty(payload, "salonTables"),
    salonLayout: hasOwnProperty(payload, "salonLayout"),
    salonReservations: hasOwnProperty(payload, "salonReservations"),
    salonRooms: hasOwnProperty(payload, "salonRooms"),
    salonTableRooms: hasOwnProperty(payload, "salonTableRooms"),
    salonFixtures: hasOwnProperty(payload, "salonFixtures"),
  };

  return {
  operationalOrders: Object.values(operationalFields).some(Boolean),
  ...operationalFields,
  waiterOps:
    hasOwnProperty(payload, "waiterOps") || hasOwnProperty(payload, "waiter_operations"),
  paymentOperations:
    hasOwnProperty(payload, "paymentOperations") ||
    hasOwnProperty(payload, "payment_operations"),
  cashRegisterState:
    hasOwnProperty(payload, "cashRegisterState") ||
    hasOwnProperty(payload, "cash_register_state"),
  kioskTerminalClaims:
    hasOwnProperty(payload, "kioskTerminalClaims") ||
    hasOwnProperty(payload, "kiosk_terminal_claims"),
  closingControl:
    hasOwnProperty(payload, "closingControl") ||
    hasOwnProperty(payload, "closing_control"),
  };
};

const mergeSharedOperationalState = (basePayload, patchPayload, fallbackFactory) => {
  const nextState = normalizeSharedOperationalState(basePayload, fallbackFactory);
  const normalizedPatch = patchPayload && typeof patchPayload === "object" ? patchPayload : {};

  if (
    hasOwnProperty(normalizedPatch, "mesasActivas") ||
    hasOwnProperty(normalizedPatch, "pedidosHistoricos") ||
    hasOwnProperty(normalizedPatch, "salonTables") ||
    hasOwnProperty(normalizedPatch, "salonLayout") ||
    hasOwnProperty(normalizedPatch, "salonReservations") ||
    hasOwnProperty(normalizedPatch, "salonRooms") ||
    hasOwnProperty(normalizedPatch, "salonTableRooms") ||
    hasOwnProperty(normalizedPatch, "salonFixtures")
  ) {
    const normalizedOrders = normalizeOperationalOrdersState(
      normalizedPatch,
      fallbackFactory
    );

    if (hasOwnProperty(normalizedPatch, "mesasActivas")) {
      nextState.mesasActivas = normalizedOrders.mesasActivas;
    }

    if (hasOwnProperty(normalizedPatch, "pedidosHistoricos")) {
      nextState.pedidosHistoricos = normalizedOrders.pedidosHistoricos;
    }

    if (hasOwnProperty(normalizedPatch, "salonTables")) {
      nextState.salonTables = normalizedOrders.salonTables;
    }

    if (hasOwnProperty(normalizedPatch, "salonLayout")) {
      nextState.salonLayout = normalizedOrders.salonLayout;
    }

    if (hasOwnProperty(normalizedPatch, "salonReservations")) {
      nextState.salonReservations = normalizedOrders.salonReservations;
    }

    if (hasOwnProperty(normalizedPatch, "salonRooms")) {
      nextState.salonRooms = normalizedOrders.salonRooms;
    }

    if (hasOwnProperty(normalizedPatch, "salonTableRooms")) {
      nextState.salonTableRooms = normalizedOrders.salonTableRooms;
    }

    if (hasOwnProperty(normalizedPatch, "salonFixtures")) {
      nextState.salonFixtures = normalizedOrders.salonFixtures;
    }
  }

  if (
    hasOwnProperty(normalizedPatch, "waiterOps") ||
    hasOwnProperty(normalizedPatch, "waiter_operations")
  ) {
    nextState.waiterOps = normalizeWaiterOperationsState(
      pickPayloadValue(normalizedPatch, "waiterOps", "waiter_operations")
    );
  }

  if (
    hasOwnProperty(normalizedPatch, "paymentOperations") ||
    hasOwnProperty(normalizedPatch, "payment_operations")
  ) {
    nextState.paymentOperations = normalizePaymentOperationsCollection(
      pickPayloadValue(normalizedPatch, "paymentOperations", "payment_operations")
    );
  }

  if (
    hasOwnProperty(normalizedPatch, "cashRegisterState") ||
    hasOwnProperty(normalizedPatch, "cash_register_state")
  ) {
    nextState.cashRegisterState = normalizeCashRegisterState(
      pickPayloadValue(normalizedPatch, "cashRegisterState", "cash_register_state")
    );
  }

  if (
    hasOwnProperty(normalizedPatch, "kioskTerminalClaims") ||
    hasOwnProperty(normalizedPatch, "kiosk_terminal_claims")
  ) {
    nextState.kioskTerminalClaims = normalizeKioskTerminalClaims(normalizedPatch);
  }

  if (
    hasOwnProperty(normalizedPatch, "closingControl") ||
    hasOwnProperty(normalizedPatch, "closing_control")
  ) {
    nextState.closingControl = normalizeBranchClosingControl(
      pickPayloadValue(normalizedPatch, "closingControl", "closing_control")
    );
  }

  return nextState;
};

const stringifyStablePayload = (value) => {
  const source =
    value && typeof value === "object" && !Array.isArray(value) ? value : {};
  const orderedKeys = Object.keys(source).sort();
  const orderedValue = {};

  orderedKeys.forEach((key) => {
    orderedValue[key] = source[key];
  });

  return JSON.stringify(orderedValue);
};

const shouldSkipDuplicateSharedOperationalWrite = (branchId, payload) => {
  const safeBranchId = String(branchId || "").trim();

  if (!safeBranchId) {
    return false;
  }

  const now = Date.now();
  const signature = stringifyStablePayload(payload);
  const previousEntry = recentSharedOperationalWriteSignatures.get(safeBranchId);

  if (
    previousEntry &&
    previousEntry.signature === signature &&
    now - previousEntry.timestamp < SHARED_OPERATIONAL_STATE_DUPLICATE_WINDOW_MS
  ) {
    return true;
  }

  recentSharedOperationalWriteSignatures.set(safeBranchId, {
    signature,
    timestamp: now,
  });

  if (recentSharedOperationalWriteSignatures.size > 48) {
    Array.from(recentSharedOperationalWriteSignatures.entries())
      .sort((left, right) => left[1].timestamp - right[1].timestamp)
      .slice(0, recentSharedOperationalWriteSignatures.size - 32)
      .forEach(([key]) => {
        recentSharedOperationalWriteSignatures.delete(key);
      });
  }

  return false;
};

const patchSharedOperationalStateViaRpc = async (
  restaurant,
  payload,
  fallbackFactory
) => {
  if (branchOperationalStatePatchRpcAvailable === false) {
    return null;
  }

  const { data, error } = await supabase.rpc("patch_branch_operational_state", {
    p_branch_id: restaurant.id,
    p_patch: payload || {},
  });

  if (error) {
    if (isMissingOperationalStatePatchRpcError(error)) {
      branchOperationalStatePatchRpcAvailable = false;
      return null;
    }

    if (isOperationalStateSyncPermissionError(error)) {
      return null;
    }

    throw new Error(
      error.message || "No se pudo sincronizar el estado operativo compartido."
    );
  }

  branchOperationalStatePatchRpcAvailable = true;

  return {
    supported: true,
    payload: normalizeSharedOperationalState(data, fallbackFactory),
  };
};

const isMissingRelationError = (error, relationName) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();
  const safeRelationName = String(relationName || "").toLowerCase();

  return (
    error?.code === "42P01" ||
    error?.code === "PGRST205" ||
    message.includes(`could not find the table 'public.${safeRelationName}'`) ||
    message.includes(`relation "public.${safeRelationName}" does not exist`) ||
    message.includes(`relation "${safeRelationName}" does not exist`)
  );
};

const isMissingRpcError = (error, rpcName) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();
  const safeRpcName = String(rpcName || "").toLowerCase();

  return (
    error?.code === "42883" ||
    error?.code === "PGRST202" ||
    message.includes(`could not find the function public.${safeRpcName}`) ||
    ((message.includes(`function public.${safeRpcName}`) ||
      message.includes(`function ${safeRpcName}`)) &&
      message.includes("does not exist"))
  );
};

const isRowLevelSecurityError = (error, relationName = "") => {
  const message = `${error?.message || ""} ${error?.details || ""} ${error?.hint || ""}`
    .toLowerCase()
    .trim();
  const safeRelationName = String(relationName || "").toLowerCase();

  return (
    error?.code === "42501" ||
    message.includes("row-level security policy") ||
    message.includes("violates row-level security policy") ||
    (safeRelationName && message.includes(safeRelationName) && message.includes("permission denied"))
  );
};

const isOutdatedKioskOrderPatchError = (error) => {
  const message = `${error?.message || ""} ${error?.details || ""}`.toLowerCase();

  return (
    message.includes("could not determine polymorphic type") &&
    message.includes("input has type unknown")
  );
};

const buildSupabaseErrorMessage = (error, fallbackMessage) => {
  const parts = [error?.message, error?.details, error?.hint]
    .map((value) => String(value || "").trim())
    .filter(Boolean);

  return parts.length > 0 ? parts.join(" ") : fallbackMessage;
};

const clonePayload = (payload, fallbackFactory) =>
  JSON.parse(
    JSON.stringify(normalizeSharedOperationalState(payload, fallbackFactory))
  );

export const canUseSharedOperationalState = () =>
  Boolean(isSupabaseConfigured && supabase);

export const getActiveKioskTerminalClaims = (payload, nowDate = new Date()) =>
  pruneInactiveKioskTerminalClaims(
    normalizeKioskTerminalClaims(payload),
    nowDate
  );

const fetchPublicKioskTerminalClaimsState = async (branchId) => {
  if (!canUseSharedOperationalState() || !branchId) {
    return {
      supported: false,
      payload: normalizeSharedOperationalState(null, EMPTY_OPERATIONAL_STATE),
      claims: [],
      updatedAt: "",
    };
  }

  const { data, error } = await supabase.rpc("get_public_kiosk_terminal_claims", {
    p_branch_id: String(branchId || "").trim(),
  });

  if (error) {
    if (
      isMissingRpcError(error, "get_public_kiosk_terminal_claims") ||
      isMissingRelationError(error, "branch_operational_state") ||
      isRowLevelSecurityError(error, "branch_operational_state")
    ) {
      return {
        supported: false,
        payload: normalizeSharedOperationalState(null, EMPTY_OPERATIONAL_STATE),
        claims: [],
        updatedAt: "",
      };
    }

    throw new Error(
      buildSupabaseErrorMessage(
        error,
        "No se pudo revisar la ocupacion publica de los totems."
      )
    );
  }

  const payloadSource =
    (data?.payload && typeof data.payload === "object" ? data.payload : null) ||
    {
      kioskTerminalClaims:
        data?.claims || data?.kioskTerminalClaims || data?.kiosk_terminal_claims || [],
    };
  const payload = normalizeSharedOperationalState(payloadSource, EMPTY_OPERATIONAL_STATE);

  return {
    supported: true,
    payload,
    claims: getActiveKioskTerminalClaims(payload),
    updatedAt: data?.updatedAt || data?.updated_at || "",
  };
};

export const fetchKioskTerminalClaimsState = async (restaurant) => {
  const directState = await fetchSharedOperationalState(
    restaurant,
    EMPTY_OPERATIONAL_STATE
  );

  if (directState.supported) {
    return {
      supported: true,
      payload: directState.payload,
      claims: getActiveKioskTerminalClaims(directState.payload),
      updatedAt: directState.updatedAt || "",
    };
  }

  const publicState = await fetchPublicKioskTerminalClaimsState(restaurant?.id);

  if (publicState.supported) {
    return publicState;
  }

  return {
    supported: false,
    payload: normalizeSharedOperationalState(null, EMPTY_OPERATIONAL_STATE),
    claims: [],
    updatedAt: "",
  };
};

export const fetchSharedOperationalState = async (
  restaurant,
  fallbackFactory,
  options = {}
) => {
  if (!canUseSharedOperationalState() || !restaurant?.id) {
    return {
      supported: false,
      payload: normalizeSharedOperationalState(null, fallbackFactory),
      availableSections: EMPTY_SHARED_STATE_SECTION_AVAILABILITY,
      updatedAt: "",
    };
  }

  let request = supabase
    .from("branch_operational_state")
    .select("payload, updated_at")
    .eq("branch_id", restaurant.id)
    .maybeSingle();

  if (options.signal && typeof request.abortSignal === "function") {
    request = request.abortSignal(options.signal);
  }

  const { data, error } = await request;

  if (error) {
    if (
      isMissingRelationError(error, "branch_operational_state") ||
      isRowLevelSecurityError(error, "branch_operational_state")
    ) {
      return {
        supported: false,
        payload: normalizeSharedOperationalState(null, fallbackFactory),
        availableSections: EMPTY_SHARED_STATE_SECTION_AVAILABILITY,
        updatedAt: "",
      };
    }

    throw new Error(
      error.message || "No se pudo leer el estado operativo compartido."
    );
  }

  return {
    supported: true,
    payload: normalizeSharedOperationalState(data?.payload, fallbackFactory),
    availableSections: getSharedStateSectionAvailability(data?.payload),
    updatedAt: data?.updated_at || "",
  };
};

export const hydrateSharedOperationalStateToLocal = async (
  restaurant,
  fallbackFactory,
  options = {}
) => {
  const result = await fetchSharedOperationalState(
    restaurant,
    fallbackFactory,
    options
  );

  if (!result.supported) {
    return result;
  }

  let savedPayload = result.payload;

  const shouldApplyOperationalState =
    typeof options.shouldApplyOperationalState !== "function" ||
    options.shouldApplyOperationalState(result) !== false;

  if (result.availableSections.operationalOrders && shouldApplyOperationalState) {
    const currentOrders = getOperationalOrdersState(restaurant, fallbackFactory);
    const remoteOrders = {
      ...currentOrders,
    };

    [
      "mesasActivas",
      "pedidosHistoricos",
      "salonTables",
      "salonLayout",
      "salonReservations",
      "salonRooms",
      "salonTableRooms",
      "salonFixtures",
    ].forEach((field) => {
      if (result.availableSections[field]) {
        remoteOrders[field] = result.payload[field];
      }
    });

    const savedOrders = saveOperationalOrdersState(
      restaurant,
      remoteOrders,
      fallbackFactory
    );

    savedPayload = {
      ...savedPayload,
      ...savedOrders,
    };
  }

  if (result.availableSections.waiterOps) {
    const savedWaiterOps = saveWaiterOperationsState(
      restaurant,
      result.payload.waiterOps,
      {
        eventSource: WAITER_OPERATIONS_REMOTE_SYNC_SOURCE,
      }
    );

    savedPayload = {
      ...savedPayload,
      waiterOps: savedWaiterOps,
    };
  }

  if (result.availableSections.paymentOperations) {
    const savedPaymentOperations = savePaymentOperations(
      restaurant,
      result.payload.paymentOperations,
      {
        eventSource: PAYMENT_OPERATIONS_REMOTE_SYNC_SOURCE,
      }
    );

    savedPayload = {
      ...savedPayload,
      paymentOperations: savedPaymentOperations,
    };
  }

  if (result.availableSections.cashRegisterState) {
    const savedCashRegisterState = saveCashRegisterState(
      restaurant,
      result.payload.cashRegisterState,
      {
        eventSource: CASH_REGISTER_REMOTE_SYNC_SOURCE,
      }
    );

    savedPayload = {
      ...savedPayload,
      cashRegisterState: savedCashRegisterState,
    };
  }

  return {
    ...result,
    payload: savedPayload,
    skippedOperationalHydration:
      result.availableSections.operationalOrders && !shouldApplyOperationalState,
  };
};

const executeSharedOperationalStatePush = async (
  restaurant,
  payload,
  fallbackFactory
) => {
  if (!canUseSharedOperationalState() || !restaurant?.id) {
    return {
      supported: false,
      payload: normalizeSharedOperationalState(payload, fallbackFactory),
    };
  }

  if (shouldSkipDuplicateSharedOperationalWrite(restaurant.id, payload)) {
    return {
      supported: true,
      payload: normalizeSharedOperationalState(payload, fallbackFactory),
    };
  }

  const rpcResult = await patchSharedOperationalStateViaRpc(
    restaurant,
    payload,
    fallbackFactory
  );

  if (rpcResult?.supported) {
    return rpcResult;
  }

  const currentState = await fetchSharedOperationalState(restaurant, fallbackFactory);

  if (!currentState.supported) {
    return {
      supported: false,
      payload: normalizeSharedOperationalState(payload, fallbackFactory),
    };
  }

  const nextPayload = clonePayload(
    mergeSharedOperationalState(currentState.payload, payload, fallbackFactory),
    fallbackFactory
  );
  const { error } = await supabase.from("branch_operational_state").upsert({
    branch_id: restaurant.id,
    payload: nextPayload,
    updated_at: new Date().toISOString(),
  });

  if (error) {
    if (
      isMissingRelationError(error, "branch_operational_state") ||
      isRowLevelSecurityError(error, "branch_operational_state")
    ) {
      return {
        supported: false,
        payload: nextPayload,
      };
    }

    throw new Error(
      error.message || "No se pudo sincronizar el estado operativo compartido."
    );
  }

  return {
    supported: true,
    payload: nextPayload,
  };
};

export const pushSharedOperationalState = (
  restaurant,
  payload,
  fallbackFactory
) => {
  const branchId = String(restaurant?.id || "").trim();

  return enqueueSharedOperationalWrite(branchId, () =>
    executeSharedOperationalStatePush(restaurant, payload, fallbackFactory)
  );
};

const executeSharedOperationalOrderPatch = async ({
  restaurant,
  orderId,
  patch = {},
  moveToHistory = false,
  historyLimit = 120,
  fallbackFactory,
}) => {
  const safeOrderId = String(orderId || "").trim();

  if (!canUseSharedOperationalState() || !restaurant?.id || !safeOrderId) {
    return {
      supported: false,
      payload: normalizeSharedOperationalState(null, fallbackFactory),
      order: null,
      reason: "No pudimos abrir la actualizacion atomica del pedido.",
    };
  }

  const { data, error } = await supabase.rpc("patch_branch_operational_order", {
    p_branch_id: restaurant.id,
    p_order_id: safeOrderId,
    p_patch: patch && typeof patch === "object" ? patch : {},
    p_move_to_history: Boolean(moveToHistory),
    p_history_limit: Math.max(1, Math.min(500, Number(historyLimit || 120))),
  });

  if (error) {
    if (
      isMissingRpcError(error, "patch_branch_operational_order") ||
      isMissingRelationError(error, "branch_operational_state")
    ) {
      return {
        supported: false,
        payload: normalizeSharedOperationalState(null, fallbackFactory),
        order: null,
        reason:
          "La base todavia no tiene habilitada la actualizacion atomica de pedidos.",
      };
    }

    throw new Error(
      buildSupabaseErrorMessage(
        error,
        "No se pudo actualizar el pedido en el estado compartido."
      )
    );
  }

  let payloadSource = data?.state || data?.payload || null;

  if (!payloadSource) {
    const refreshedState = await fetchSharedOperationalState(
      restaurant,
      fallbackFactory
    );

    if (refreshedState.supported) {
      payloadSource = refreshedState.payload;
    }
  }

  return {
    supported: true,
    payload: normalizeSharedOperationalState(payloadSource, fallbackFactory),
    order: data?.order || null,
    moveToHistory:
      data?.moveToHistory === true || data?.move_to_history === true,
    updatedAt: data?.updatedAt || data?.updated_at || "",
  };
};

export const patchSharedOperationalOrder = (options = {}) =>
  enqueueSharedOperationalWrite(options?.restaurant?.id, () =>
    executeSharedOperationalOrderPatch(options)
  );

export const setSharedOrderDiscount = ({ restaurant, order, discount, settleZero = false, requestId }) =>
  withRequestDeadline((signal) => enqueueSharedOperationalWrite(restaurant?.id, async () => {
    if (signal.aborted) throw new Error("La espera termino. Actualiza la cuenta antes de reintentar.");
    if (!canUseSharedOperationalState() || !restaurant?.id || !order?.id) {
      throw new Error("Necesitas conexion con el restaurante para cambiar la cuenta.");
    }
    const { data, error } = await supabase.rpc("set_branch_order_discount", {
      p_branch_id: restaurant.id,
      p_order_id: order.id,
      p_type: discount?.type || "none",
      p_value: Number(discount?.value || 0),
      p_expected_total: Number(order.pricing?.finalTotal ?? order.totalAmount),
      p_expected_revision: order.pricing?.discountRevision || "",
      p_request_id: requestId,
      p_settle_zero: settleZero,
    }).abortSignal(signal);
    if (error) throw new Error(error.message || "No pudimos guardar el descuento.");
    if (!data?.order || !data?.state) throw new Error("La base no confirmo el cambio. Actualiza la cuenta.");
    return { order: data.order, payload: normalizeSharedOperationalState(data.state) };
  }), {
    message: "No pudimos confirmar el cambio a tiempo. Actualiza la cuenta para comprobar si se guardo antes de reintentar.",
  });

export const getSharedOrderBill = async (restaurant, orderId) => {
  if (!canUseSharedOperationalState() || !restaurant?.id) throw new Error("Necesitas conexion para verificar el total de la cuenta.");
  const { data, error } = await withRequestDeadline((signal) => supabase.rpc("get_branch_order_bill", {
    p_branch_id: restaurant.id, p_order_id: orderId,
  }).abortSignal(signal));
  if (error || !data?.id) throw new Error(error?.message || "No pudimos verificar la cuenta para imprimir.");
  return data;
};

// Claims a payment and updates its operational order while holding the same
// database row lock. This prevents Caja and Mozo from confirming it twice.
export const claimSharedOperationalOrderPayment = async ({
  restaurant,
  orderId,
  paymentOperation,
  orderPatch = {},
  paymentLimit = 80,
  fallbackFactory,
}) => {
  const safeOrderId = String(orderId || "").trim();
  const safeOperationId = String(paymentOperation?.id || "").trim();

  if (
    !canUseSharedOperationalState() ||
    !restaurant?.id ||
    !safeOrderId ||
    !safeOperationId
  ) {
    return {
      supported: false,
      claimed: false,
      reason: "No pudimos abrir la confirmacion coordinada del cobro.",
      payload: normalizeSharedOperationalState(null, fallbackFactory),
      order: null,
      paymentOperation: null,
    };
  }

  const { data, error } = await supabase.rpc(
    "claim_branch_operational_order_payment",
    {
      p_branch_id: restaurant.id,
      p_order_id: safeOrderId,
      p_payment_operation: paymentOperation,
      p_order_patch:
        orderPatch && typeof orderPatch === "object" ? orderPatch : {},
      p_payment_limit: Math.max(1, Math.min(400, Number(paymentLimit || 80))),
    }
  );

  if (error) {
    if (
      isMissingRpcError(error, "claim_branch_operational_order_payment") ||
      isMissingRelationError(error, "branch_operational_state")
    ) {
      return {
        supported: false,
        claimed: false,
        reason:
          "La base todavia no tiene habilitada la confirmacion coordinada de cobros.",
        payload: normalizeSharedOperationalState(null, fallbackFactory),
        order: null,
        paymentOperation: null,
      };
    }

    throw new Error(
      buildSupabaseErrorMessage(
        error,
        "No se pudo confirmar el cobro en el estado compartido."
      )
    );
  }

  let payloadSource = data?.state || data?.payload || null;

  if (!payloadSource) {
    const refreshedState = await fetchSharedOperationalState(
      restaurant,
      fallbackFactory
    );

    if (refreshedState.supported) {
      payloadSource = refreshedState.payload;
    }
  }

  return {
    supported: true,
    claimed: data?.claimed === true,
    reason: String(data?.reason || "").trim(),
    payload: normalizeSharedOperationalState(payloadSource, fallbackFactory),
    order: data?.order || null,
    paymentOperation: data?.paymentOperation || data?.payment_operation || null,
    existingPaymentOperation:
      data?.existingPaymentOperation || data?.existing_payment_operation || null,
    updatedAt: data?.updatedAt || data?.updated_at || "",
  };
};

export const pushSharedWaiterOperationsState = async (
  restaurant,
  waiterOperations
) =>
  pushSharedOperationalState(restaurant, {
    waiterOps: waiterOperations,
  });

export const pushSharedPaymentOperationsState = async (
  restaurant,
  paymentOperations
) =>
  pushSharedOperationalState(restaurant, {
    paymentOperations,
  });

export const pushSharedCashRegisterState = async (
  restaurant,
  cashRegisterState
) =>
  pushSharedOperationalState(restaurant, {
    cashRegisterState,
  });

const executeSharedBranchClosingControlPush = async (
  restaurant,
  closingControl
) => {
  const normalizedControl = normalizeBranchClosingControl(closingControl);

  if (!canUseSharedOperationalState() || !restaurant?.id) {
    return {
      supported: false,
      payload: normalizeSharedOperationalState({
        closingControl: normalizedControl,
      }),
    };
  }

  if (branchClosingControlRpcAvailable === false) {
    return {
      supported: false,
      payload: normalizeSharedOperationalState({
        closingControl: normalizedControl,
      }),
    };
  }

  const { data, error } = await supabase.rpc("set_branch_closing_control", {
    p_branch_id: restaurant.id,
    p_closing_control: normalizedControl,
  });

  if (error) {
    if (isMissingRpcError(error, "set_branch_closing_control")) {
      branchClosingControlRpcAvailable = false;
      return {
        supported: false,
        payload: normalizeSharedOperationalState({
          closingControl: normalizedControl,
        }),
      };
    }

    if (isOperationalStateSyncPermissionError(error)) {
      return {
        supported: false,
        payload: normalizeSharedOperationalState({
          closingControl: normalizedControl,
        }),
      };
    }

    throw new Error(
      error.message || "No se pudo sincronizar la decision de cierre de la sucursal."
    );
  }

  branchClosingControlRpcAvailable = true;

  return {
    supported: true,
    payload: normalizeSharedOperationalState(data),
  };
};

export const pushSharedBranchClosingControl = (
  restaurant,
  closingControl
) => {
  const branchId = String(restaurant?.id || "").trim();

  return enqueueSharedOperationalWrite(branchId, () =>
    executeSharedBranchClosingControlPush(restaurant, closingControl)
  );
};

export const submitPublicQrOrderToSharedState = async ({
  qrToken,
  customerName,
  customerSessionToken,
  orderSnapshot,
  checkoutMethod,
  fallbackFactory,
}) => {
  if (!canUseSharedOperationalState()) {
    return {
      supported: false,
      reason:
        "Supabase no esta configurado en este entorno. No pudimos abrir la capa compartida para pedidos QR.",
      payload: normalizeSharedOperationalState(null, fallbackFactory),
      order: null,
      customerSessionToken: customerSessionToken || "",
    };
  }

  const { data, error } = await supabase.rpc("submit_public_qr_order", {
    p_qr_token: String(qrToken || "").trim(),
    p_customer_name: String(customerName || "").trim(),
    p_customer_session_token: String(customerSessionToken || "").trim(),
    p_order_snapshot: orderSnapshot || {},
    p_payment_method: String(checkoutMethod || "").trim(),
  });

  if (error) {
    if (
      isMissingRpcError(error, "submit_public_qr_order") ||
      isMissingRelationError(error, "branch_operational_state")
    ) {
      return {
        supported: false,
        reason:
          "La base todavia no tiene habilitada la sincronizacion de pedidos QR (submit_public_qr_order / branch_operational_state). Reaplica operational_state_patch.sql, qr_customer_session_expiration_patch.sql y qr_access_window_patch.sql en ese orden.",
        payload: normalizeSharedOperationalState(null, fallbackFactory),
        order: null,
        customerSessionToken: customerSessionToken || "",
      };
    }

    throw new Error(
      buildSupabaseErrorMessage(error, "No se pudo enviar el pedido por QR.")
    );
  }

  return {
    supported: true,
    payload: normalizeSharedOperationalState(
      data?.state || data?.payload || null,
      fallbackFactory
    ),
    order: data?.order || null,
    terminal: data?.terminal === true,
    terminalReason:
      data?.terminalReason || data?.terminal_reason || "",
    customerSessionToken:
      data?.customerSessionToken ||
      data?.customer_session_token ||
    customerSessionToken ||
      "",
  };
};

export const cancelPublicQrOrderFromSharedState = async ({
  qrToken,
  customerSessionToken,
  orderId,
  fallbackFactory,
}) => {
  if (!canUseSharedOperationalState()) {
    return {
      supported: false,
      cancelled: false,
      alreadyCancelled: false,
      stockRestored: false,
      reason:
        "Supabase no esta configurado en este entorno. No pudimos cancelar el pedido QR.",
      payload: normalizeSharedOperationalState(null, fallbackFactory),
      order: null,
    };
  }

  const { data, error } = await supabase.rpc("cancel_public_qr_order", {
    p_qr_token: String(qrToken || "").trim(),
    p_customer_session_token: String(customerSessionToken || "").trim(),
    p_order_id: String(orderId || "").trim(),
  });

  if (error) {
    if (
      isMissingRpcError(error, "cancel_public_qr_order") ||
      isMissingRelationError(error, "branch_operational_state")
    ) {
      return {
        supported: false,
        cancelled: false,
        alreadyCancelled: false,
        stockRestored: false,
        reason:
          "La base todavia no tiene habilitada la cancelacion segura de pedidos QR (cancel_public_qr_order). Reaplica public_qr_order_cancellation_patch.sql.",
        payload: normalizeSharedOperationalState(null, fallbackFactory),
        order: null,
      };
    }

    throw new Error(
      buildSupabaseErrorMessage(
        error,
        "No se pudo cancelar el pedido por QR."
      )
    );
  }

  return {
    supported: true,
    cancelled: data?.cancelled === true,
    alreadyCancelled:
      data?.alreadyCancelled === true || data?.already_cancelled === true,
    stockRestored:
      data?.stockRestored === true || data?.stock_restored === true,
    reason: String(data?.reason || "").trim(),
    payload: normalizeSharedOperationalState(
      data?.state || data?.payload || null,
      fallbackFactory
    ),
    order: data?.order || null,
  };
};

export const submitPublicKioskOrderToSharedState = async ({
  branchId,
  stationNumber,
  customerName,
  customerSessionToken,
  orderSnapshot,
  checkoutMethod,
  fallbackFactory,
}) => {
  if (!canUseSharedOperationalState()) {
    return {
      supported: false,
      reason:
        "Supabase no esta configurado en este entorno. No pudimos abrir la capa compartida para pedidos del kiosk.",
      payload: normalizeSharedOperationalState(null, fallbackFactory),
      order: null,
      customerSessionToken: customerSessionToken || "",
    };
  }

  const { data, error } = await supabase.rpc("submit_public_kiosk_order", {
    p_branch_id: String(branchId || "").trim(),
    p_station_number: Math.min(12, Math.max(1, Number(stationNumber || 1) || 1)),
    p_customer_name: String(customerName || "").trim(),
    p_customer_session_token: String(customerSessionToken || "").trim(),
    p_order_snapshot: orderSnapshot || {},
    p_payment_method: String(checkoutMethod || "").trim(),
  });

  if (error) {
    if (
      isMissingRpcError(error, "submit_public_kiosk_order") ||
      isMissingRelationError(error, "branch_operational_state") ||
      isOutdatedKioskOrderPatchError(error)
    ) {
      return {
        supported: false,
        reason:
          "La base todavia no tiene actualizada la sincronizacion publica de pedidos kiosk. Aplica nuevamente public_kiosk_order_submission_patch.sql en Supabase para corregir submit_public_kiosk_order.",
        payload: normalizeSharedOperationalState(null, fallbackFactory),
        order: null,
        customerSessionToken: customerSessionToken || "",
      };
    }

    throw new Error(
      buildSupabaseErrorMessage(error, "No se pudo enviar el pedido del kiosk.")
    );
  }

  return {
    supported: true,
    payload: normalizeSharedOperationalState(
      data?.state || data?.payload || null,
      fallbackFactory
    ),
    order: data?.order || null,
    terminal: data?.terminal === true,
    terminalReason:
      data?.terminalReason || data?.terminal_reason || "",
    customerSessionToken:
      data?.customerSessionToken ||
      data?.customer_session_token ||
      customerSessionToken ||
      "",
  };
};

export const submitPublicWaiterServiceRequestToSharedState = async ({
  qrToken,
  customerName,
  customerSessionToken,
  requestSnapshot,
}) => {
  if (!canUseSharedOperationalState()) {
    return {
      supported: false,
      reason:
        "Supabase no esta configurado en este entorno. No pudimos abrir la capa compartida para el llamado al mozo.",
      request: null,
      alert: null,
      waiterAssignment: null,
    };
  }

  const { data, error } = await supabase.rpc(
    "submit_public_waiter_service_request",
    {
      p_qr_token: String(qrToken || "").trim(),
      p_customer_name: String(customerName || "").trim(),
      p_customer_session_token: String(customerSessionToken || "").trim(),
      p_request_snapshot: requestSnapshot || {},
    }
  );

  if (error) {
    if (
      isMissingRpcError(error, "submit_public_waiter_service_request") ||
      isMissingRelationError(error, "branch_operational_state")
    ) {
      return {
        supported: false,
        reason:
          "La base todavia no tiene habilitada la sincronizacion del llamado al mozo (submit_public_waiter_service_request / branch_operational_state).",
        request: null,
        alert: null,
        waiterAssignment: null,
      };
    }

    throw new Error(
      buildSupabaseErrorMessage(
        error,
        "No se pudo registrar el llamado al mozo."
      )
    );
  }

  return {
    supported: true,
    alreadyPending:
      data?.alreadyPending === true || data?.already_pending === true,
    request: data?.request || null,
    alert: data?.alert || null,
    waiterAssignment:
      data?.waiterAssignment || data?.waiter_assignment || null,
  };
};

export const claimKioskTerminalStationInSharedState = async (
  restaurant,
  { stationNumber, deviceId, deviceLabel = "" } = {}
) => {
  const safeDeviceId = String(deviceId || "").trim().slice(0, 120);
  const safeStationNumber = normalizeKioskTerminalStationNumber(stationNumber);

  if (!restaurant?.id || !safeDeviceId || !safeStationNumber) {
    return {
      supported: false,
      conflict: false,
      claims: [],
      conflictingClaim: null,
    };
  }

  let currentState = await fetchSharedOperationalState(
    restaurant,
    EMPTY_OPERATIONAL_STATE
  );
  let claimAccessMode = currentState.supported ? "table" : "local_fallback";

  if (!currentState.supported) {
    const publicState = await fetchPublicKioskTerminalClaimsState(restaurant.id);

    if (publicState.supported) {
      currentState = publicState;
      claimAccessMode = "public_rpc";
    }
  }

  const activeClaims = getActiveKioskTerminalClaims(currentState.payload);
  const conflictingClaim =
    activeClaims.find(
      (claim) =>
        claim.stationNumber === safeStationNumber &&
        claim.deviceId !== safeDeviceId
    ) || null;

  if (conflictingClaim) {
    return {
      supported: currentState.supported,
      conflict: true,
      claims: activeClaims,
      conflictingClaim,
      payload: currentState.payload,
    };
  }

  const nowIso = new Date().toISOString();
  const previousClaimForDevice = activeClaims.find(
    (claim) => claim.deviceId === safeDeviceId
  );
  const nextClaims = [
    ...activeClaims.filter(
      (claim) =>
        claim.deviceId !== safeDeviceId &&
        claim.stationNumber !== safeStationNumber
    ),
    {
      stationNumber: safeStationNumber,
      stationLabel: `Totem ${safeStationNumber}`,
      deviceId: safeDeviceId,
      deviceLabel: String(deviceLabel || "").trim().slice(0, 120),
      claimedAt: previousClaimForDevice?.claimedAt || nowIso,
      lastSeenAt: nowIso,
    },
  ].sort((left, right) => left.stationNumber - right.stationNumber);

  if (claimAccessMode === "public_rpc") {
    const { data, error } = await supabase.rpc("claim_public_kiosk_terminal", {
      p_branch_id: String(restaurant.id || "").trim(),
      p_station_number: safeStationNumber,
      p_device_id: safeDeviceId,
      p_device_label: String(deviceLabel || "").trim().slice(0, 120),
    });

    if (error) {
      if (
        isMissingRpcError(error, "claim_public_kiosk_terminal") ||
        isMissingRelationError(error, "branch_operational_state") ||
        isRowLevelSecurityError(error, "branch_operational_state")
      ) {
        return {
          supported: false,
          conflict: false,
          claims: nextClaims,
          conflictingClaim: null,
          payload: {
            ...currentState.payload,
            kioskTerminalClaims: nextClaims,
          },
        };
      }

      throw new Error(
        buildSupabaseErrorMessage(
          error,
          "No se pudo reservar este totem desde el launcher publico."
        )
      );
    }

    const payloadSource =
      (data?.payload && typeof data.payload === "object" ? data.payload : null) ||
      {
        kioskTerminalClaims:
          data?.claims || data?.kioskTerminalClaims || data?.kiosk_terminal_claims || [],
      };
    const payload = normalizeSharedOperationalState(
      payloadSource,
      EMPTY_OPERATIONAL_STATE
    );
    const refreshedClaims = getActiveKioskTerminalClaims(payload);
    const refreshedClaim =
      data?.conflictingClaim ||
      data?.conflicting_claim ||
      refreshedClaims.find((claim) => claim.stationNumber === safeStationNumber) ||
      null;
    const hasConflict =
      data?.conflict === true ||
      Boolean(refreshedClaim && refreshedClaim.deviceId !== safeDeviceId);

    return {
      supported: true,
      conflict: hasConflict,
      claims: refreshedClaims,
      conflictingClaim: hasConflict ? refreshedClaim : null,
      payload,
    };
  }

  const pushResult = await pushSharedOperationalState(
    restaurant,
    {
      kioskTerminalClaims: nextClaims,
    },
    EMPTY_OPERATIONAL_STATE
  );

  if (!pushResult.supported) {
    return {
      supported: false,
      conflict: false,
      claims: nextClaims,
      conflictingClaim: null,
      payload: {
        ...currentState.payload,
        kioskTerminalClaims: nextClaims,
      },
    };
  }

  const refreshedState = await fetchSharedOperationalState(
    restaurant,
    EMPTY_OPERATIONAL_STATE
  );
  const refreshedClaims = getActiveKioskTerminalClaims(refreshedState.payload);
  const refreshedClaim =
    refreshedClaims.find((claim) => claim.stationNumber === safeStationNumber) ||
    null;

  if (refreshedClaim && refreshedClaim.deviceId !== safeDeviceId) {
    return {
      supported: true,
      conflict: true,
      claims: refreshedClaims,
      conflictingClaim: refreshedClaim,
      payload: refreshedState.payload,
    };
  }

  return {
    supported: true,
    conflict: false,
    claims: refreshedClaims,
    conflictingClaim: null,
    payload: refreshedState.payload,
  };
};

export const releaseKioskTerminalStationInSharedState = async (
  restaurant,
  { stationNumber, deviceId } = {}
) => {
  const safeDeviceId = String(deviceId || "").trim().slice(0, 120);
  const safeStationNumber = normalizeKioskTerminalStationNumber(stationNumber);

  if (!restaurant?.id || !safeDeviceId) {
    return {
      supported: false,
      claims: [],
    };
  }

  let currentState = await fetchSharedOperationalState(
    restaurant,
    EMPTY_OPERATIONAL_STATE
  );
  let claimAccessMode = currentState.supported ? "table" : "local_fallback";

  if (!currentState.supported) {
    const publicState = await fetchPublicKioskTerminalClaimsState(restaurant.id);

    if (publicState.supported) {
      currentState = publicState;
      claimAccessMode = "public_rpc";
    }
  }

  const activeClaims = getActiveKioskTerminalClaims(currentState.payload);
  const nextClaims = activeClaims.filter((claim) => {
    if (claim.deviceId !== safeDeviceId) {
      return true;
    }

    if (!safeStationNumber) {
      return false;
    }

    return claim.stationNumber !== safeStationNumber;
  });

  if (claimAccessMode === "public_rpc") {
    const { data, error } = await supabase.rpc("release_public_kiosk_terminal", {
      p_branch_id: String(restaurant.id || "").trim(),
      p_station_number: safeStationNumber || null,
      p_device_id: safeDeviceId,
    });

    if (error) {
      if (
        isMissingRpcError(error, "release_public_kiosk_terminal") ||
        isMissingRelationError(error, "branch_operational_state") ||
        isRowLevelSecurityError(error, "branch_operational_state")
      ) {
        return {
          supported: false,
          claims: nextClaims,
        };
      }

      throw new Error(
        buildSupabaseErrorMessage(
          error,
          "No se pudo liberar este totem desde el launcher publico."
        )
      );
    }

    const payloadSource =
      (data?.payload && typeof data.payload === "object" ? data.payload : null) ||
      {
        kioskTerminalClaims:
          data?.claims || data?.kioskTerminalClaims || data?.kiosk_terminal_claims || [],
      };
    const payload = normalizeSharedOperationalState(
      payloadSource,
      EMPTY_OPERATIONAL_STATE
    );

    return {
      supported: true,
      claims: getActiveKioskTerminalClaims(payload),
    };
  }

  const pushResult = await pushSharedOperationalState(
    restaurant,
    {
      kioskTerminalClaims: nextClaims,
    },
    EMPTY_OPERATIONAL_STATE
  );

  return {
    supported: pushResult.supported,
    claims: nextClaims,
  };
};
